import type { ProgressState } from "../application/progress-state";
import {
  observeProgressMutation,
  type ProgressMutation,
} from "../application/progress-mutation";
import {
  getSkinConfig,
  type SkinSelectionStatus,
  type SkinUnlockStatus,
} from "../application/skin-rules";
import { getStartingSizeBenefit } from "../application/upgrade-rules";
import { gameplayConfig, type SkinId } from "../config/gameplay-config";
import { renderSnakeVisual } from "./snake-visual";

export type SkinShellSelectionStatus =
  SkinSelectionStatus | "persistence-failed";
export type SkinShellUnlockStatus = SkinUnlockStatus | "persistence-failed";
export type SkinShellTimer = ReturnType<typeof globalThis.setTimeout>;

export interface SkinShellScheduler {
  clear(timer: SkinShellTimer): void;
  schedule(callback: () => void, delayMs: number): SkinShellTimer;
}

export interface SkinShellStore {
  getState(): ProgressState;
  selectSkin(
    id: string,
    expectedSelectedSkinId: string,
  ): ProgressMutation<SkinShellSelectionStatus>;
  unlockSkin(
    id: string,
    expectedCoins: number,
  ): ProgressMutation<SkinShellUnlockStatus>;
}

export interface SkinShellElements {
  readonly coins: HTMLElement;
  readonly list: HTMLElement;
  readonly nextButton: HTMLButtonElement;
  readonly position: HTMLElement;
  readonly preview: HTMLElement;
  readonly previousButton: HTMLButtonElement;
  readonly screen: HTMLElement;
  readonly status: HTMLElement;
  readonly studio: HTMLElement;
}

interface SkinCardElements {
  readonly button: HTMLButtonElement;
  readonly card: HTMLElement;
  readonly cost: HTMLElement;
  readonly handler: () => void;
  readonly id: SkinId;
  readonly state: HTMLElement;
}

const celebrationDurationMs = 900;

const browserScheduler: SkinShellScheduler = {
  clear: (timer) => {
    globalThis.clearTimeout(timer);
  },
  schedule: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
};

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing skin shell element: ${id}`);
  }
  return element;
};

const appendTextElement = (
  documentScope: Document,
  parent: HTMLElement,
  tagName: "span" | "strong",
  className: string,
  text: string,
): HTMLElement => {
  const element = documentScope.createElement(tagName);
  element.className = className;
  element.textContent = text;
  parent.appendChild(element);
  return element;
};

const unlockStatusCopy = (
  name: string,
  status: SkinShellUnlockStatus,
): string => {
  const copies: Readonly<Record<SkinShellUnlockStatus, string>> = {
    "already-unlocked": `${name} is already unlocked.`,
    "insufficient-funds": "Eat smaller snakes to earn coins.",
    "invalid-skin": "That skin is not available.",
    "invalid-state": "Skin progress is not available right now.",
    "persistence-failed": "The skin was not saved. Your coins are unchanged.",
    "stale-command": "That skin action is out of date. Try again.",
    unlocked: `${name} unlocked. Tap Wear.`,
  };
  return copies[status];
};

const selectionStatusCopy = (
  name: string,
  status: SkinShellSelectionStatus,
): string => {
  const copies: Readonly<Record<SkinShellSelectionStatus, string>> = {
    "already-selected": `${name} is already selected.`,
    "invalid-skin": "That skin is not available.",
    "invalid-state": "Skin progress is not available right now.",
    locked: `${name} must be unlocked first.`,
    "persistence-failed": "The selection was not saved.",
    selected: `Wearing ${name}.`,
    "stale-command": "That selection is out of date. Try again.",
  };
  return copies[status];
};

export class SkinShell {
  private readonly cards: readonly SkinCardElements[];
  private celebrationTimer: SkinShellTimer | null = null;
  private focusedIndex: number;
  private mutationPending = false;

  public constructor(
    documentScope: Document,
    private readonly store: SkinShellStore,
    private readonly elements: SkinShellElements,
    private readonly scheduler: SkinShellScheduler = browserScheduler,
  ) {
    const selectedSkinId = store.getState().selectedSkinId;
    this.focusedIndex = gameplayConfig.skins.findIndex(
      (skin) => skin.id === selectedSkinId,
    );
    this.cards = gameplayConfig.skins.map((skin) => {
      const card = documentScope.createElement("article");
      card.className = "snakely-skin-card";
      card.setAttribute("data-skin-id", skin.id);
      appendTextElement(
        documentScope,
        card,
        "strong",
        "snakely-skin-name",
        skin.displayName,
      );
      const state = appendTextElement(
        documentScope,
        card,
        "span",
        "snakely-skin-state",
        "",
      );
      const cost = appendTextElement(
        documentScope,
        card,
        "span",
        "snakely-skin-cost",
        "",
      );
      const button = documentScope.createElement("button");
      button.className = "snakely-button snakely-skin-button";
      button.type = "button";
      const handler = (): void => {
        this.activate(skin.id);
      };
      button.addEventListener("click", handler);
      card.appendChild(button);
      this.elements.list.appendChild(card);
      return { button, card, cost, handler, id: skin.id, state };
    });
    elements.previousButton.addEventListener("click", this.handlePrevious);
    elements.nextButton.addEventListener("click", this.handleNext);
    this.render();
  }

  public destroy(): void {
    this.clearCelebration();
    this.elements.previousButton.removeEventListener(
      "click",
      this.handlePrevious,
    );
    this.elements.nextButton.removeEventListener("click", this.handleNext);
    for (const card of this.cards) {
      card.button.removeEventListener("click", card.handler);
    }
  }

  public render(): void {
    if (this.elements.screen.hidden) {
      this.clearCelebration();
    }
    const progress = this.store.getState();
    this.elements.coins.textContent = `${String(progress.totalCoins)} coins`;
    for (const [index, card] of this.cards.entries()) {
      const skin = getSkinConfig(card.id);
      const selected = progress.selectedSkinId === card.id;
      const unlocked = progress.unlockedSkinIds.includes(card.id);
      const affordable =
        !unlocked &&
        skin.unlockCost !== null &&
        progress.totalCoins >= skin.unlockCost;
      const state = selected
        ? "selected"
        : unlocked
          ? "unlocked"
          : affordable
            ? "affordable"
            : "locked";
      card.card.hidden = index !== this.focusedIndex;
      card.card.setAttribute("data-state", state);
      card.button.setAttribute("aria-pressed", String(selected));
      card.button.setAttribute(
        "aria-label",
        `${selected ? "Wearing" : unlocked ? "Wear" : "Unlock"} ${skin.displayName}`,
      );
      card.button.disabled = selected || this.mutationPending;
      if (selected) {
        card.state.textContent = "";
        card.cost.textContent = "";
        card.button.textContent = "✓ Wearing";
      } else if (unlocked) {
        card.state.textContent = "";
        card.cost.textContent = "";
        card.button.textContent = "Wear";
      } else if (affordable) {
        card.state.textContent = "";
        card.cost.textContent = `🔓 ${String(skin.unlockCost)} coins`;
        card.button.textContent = "Unlock";
      } else {
        const cost = Number(skin.unlockCost);
        card.state.textContent = "";
        card.cost.textContent = `🔒 ${String(cost)} coins`;
        card.button.textContent = "Unlock";
      }
    }
    this.elements.previousButton.disabled = this.focusedIndex === 0;
    this.elements.nextButton.disabled =
      this.focusedIndex === gameplayConfig.skins.length - 1;
    this.elements.position.textContent = `${String(this.focusedIndex + 1)} of ${String(gameplayConfig.skins.length)}`;
    this.renderPreview(progress);
  }

  private readonly handlePrevious = (): void => {
    this.showIndex(this.focusedIndex - 1);
  };

  private readonly handleNext = (): void => {
    this.showIndex(this.focusedIndex + 1);
  };

  private activate(id: SkinId): void {
    const progress = this.store.getState();
    const skin = getSkinConfig(id);
    if (progress.unlockedSkinIds.includes(id)) {
      const mutation = this.store.selectSkin(id, progress.selectedSkinId);
      observeProgressMutation(mutation, {
        complete: (result) => {
          this.mutationPending = false;
          this.elements.status.textContent = selectionStatusCopy(
            skin.displayName,
            result,
          );
          if (result === "selected") {
            this.startCelebration("selected");
          }
          this.render();
        },
        failed: () => {
          this.mutationPending = false;
          this.elements.status.textContent = selectionStatusCopy(
            skin.displayName,
            "persistence-failed",
          );
          this.render();
        },
        pending: () => {
          this.mutationPending = true;
          this.elements.status.textContent = `Saving ${skin.displayName}…`;
          this.render();
        },
      });
    } else {
      const mutation = this.store.unlockSkin(id, progress.totalCoins);
      observeProgressMutation(mutation, {
        complete: (result) => {
          this.mutationPending = false;
          this.elements.status.textContent = unlockStatusCopy(
            skin.displayName,
            result,
          );
          if (result === "unlocked") {
            this.startCelebration("unlocked");
          }
          this.render();
        },
        failed: () => {
          this.mutationPending = false;
          this.elements.status.textContent = unlockStatusCopy(
            skin.displayName,
            "persistence-failed",
          );
          this.render();
        },
        pending: () => {
          this.mutationPending = true;
          this.elements.status.textContent = `Saving ${skin.displayName}…`;
          this.render();
        },
      });
    }
  }

  private clearCelebration(): void {
    if (this.celebrationTimer !== null) {
      this.scheduler.clear(this.celebrationTimer);
      this.celebrationTimer = null;
    }
    delete this.elements.studio.dataset.celebration;
  }

  private renderPreview(progress: ProgressState): void {
    const skin = gameplayConfig.skins[this.focusedIndex];
    if (skin === undefined) {
      throw new Error("Focused skin is unavailable");
    }
    const level = getStartingSizeBenefit(progress.upgrades["starting-size"]);
    renderSnakeVisual(
      this.elements.preview,
      skin.id,
      level,
      `${skin.displayName} Level ${String(level)} snake preview`,
    );
  }

  private showIndex(index: number): void {
    if (index < 0 || index >= this.cards.length) {
      return;
    }
    this.focusedIndex = index;
    this.elements.status.textContent = "";
    this.render();
    for (const card of this.cards.slice(index, index + 1)) {
      card.button.focus();
    }
  }

  private startCelebration(kind: "selected" | "unlocked"): void {
    this.clearCelebration();
    this.elements.studio.dataset.celebration = kind;
    this.celebrationTimer = this.scheduler.schedule(() => {
      this.clearCelebration();
    }, celebrationDurationMs);
  }
}

export const installSkinShell = (
  documentScope: Document,
  store: SkinShellStore,
): SkinShell =>
  new SkinShell(documentScope, store, {
    coins: requireElement(documentScope, "customize-coins"),
    list: requireElement(documentScope, "skin-list"),
    nextButton: requireElement(
      documentScope,
      "skin-next-button",
    ) as HTMLButtonElement,
    position: requireElement(documentScope, "skin-position"),
    preview: requireElement(documentScope, "skin-preview"),
    previousButton: requireElement(
      documentScope,
      "skin-previous-button",
    ) as HTMLButtonElement,
    screen: requireElement(documentScope, "settings-customize-panel"),
    status: requireElement(documentScope, "skin-status"),
    studio: requireElement(documentScope, "skin-studio"),
  });
